import { Component, OnDestroy, OnInit, forwardRef } from '@angular/core'
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms'
import { Subscription } from 'rxjs'
import { SpotByNetAppIntegrationConfig } from 'src/app/account/services/account.service'
import { UiService } from 'src/app/services/ui.service'
import { subscriptionActive } from 'src/utils/rxjs-utils'

const actStr = 'act-'

@Component({
  selector: 'spot-by-netapp-settings',
  templateUrl: './spot-by-netapp-settings.component.html',
  styleUrls: ['./spot-by-netapp-settings.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SpotByNetappSettingsComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SpotByNetappSettingsComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class SpotByNetappSettingsComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  private configChangeFunc: any = null
  apiKeyForm = this.formBuilder.group({
    primaryAccountId: ['', [Validators.required, Validators.pattern('^act-[a-zA-Z0-9]{8}$')]],
    primaryApiKey: ['', [Validators.required]],
    recoveryAccountId: ['', [Validators.required, Validators.pattern('^act-[a-zA-Z0-9]{8}$')]],
    recoveryApiKey: ['', [Validators.required]],
  })
  formChangedSubscription: Subscription = null

  private valueInitialized = false

  constructor(
    private formBuilder: FormBuilder,
    public uiService: UiService
  ) {}

  ngOnInit(): void {
    this.formChangedSubscription = this.apiKeyForm.valueChanges.subscribe((newVal) => {
      let primaryAccountId = newVal.primaryAccountId
      let primaryApiKey = this.apiKeyForm.controls.primaryApiKey.dirty ? newVal.primaryApiKey : null
      let recoveryAccountId = newVal.recoveryAccountId
      let recoveryApiKey = this.apiKeyForm.controls.recoveryApiKey.dirty ? newVal.recoveryApiKey : null
      if (this.configChangeFunc) {
        this.configChangeFunc(
          new SpotByNetAppIntegrationConfig(primaryAccountId, primaryApiKey, recoveryAccountId, recoveryApiKey)
        )
      }
    })
  }

  ngOnDestroy(): void {
    if (subscriptionActive(this.formChangedSubscription)) {
      this.formChangedSubscription.unsubscribe()
    }
    this.formChangedSubscription = null
  }

  writeValue(config: SpotByNetAppIntegrationConfig): void {
    if (!!config) {
      this.apiKeyForm.setValue({
        primaryAccountId: config.sourceAccountId,
        primaryApiKey: config.sourceAuthToken,
        recoveryAccountId: config.targetAccountId,
        recoveryApiKey: config.targetAuthToken,
      })
    }
    this.valueInitialized = true
    if (!!this.configChangeFunc) {
      // publish a config with the same account IDs but null auth tokens.  This reinitializes the parent form
      // so that it doesn't remember the masked auth tokens that get passed in, and therefore won't overwrite
      // the good auth tokens on the server with these bad masked auth tokens.
      this.configChangeFunc(
        new SpotByNetAppIntegrationConfig(config.sourceAccountId, null, config.targetAccountId, null)
      )
    }
  }

  registerOnChange(fn: any): void {
    this.configChangeFunc = fn
    if (this.valueInitialized) {
      // publish a config with the same account IDs but null auth tokens.  This reinitializes the parent form
      // so that it doesn't remember the masked auth tokens that get passed in, and therefore won't overwrite
      // the good auth tokens on the server with these bad masked auth tokens.
      this.configChangeFunc(
        new SpotByNetAppIntegrationConfig(
          this.apiKeyForm.value.primaryAccountId,
          null,
          this.apiKeyForm.value.recoveryAccountId,
          null
        )
      )
    }
  }

  registerOnTouched(fn: any): void {}

  validate(control: FormControl): ValidationErrors {
    if (!this.apiKeyForm.valid) {
      return {
        apiKeyForm: 'is not valid',
      }
    }
    return null
  }

  private isBadAccountIdPrefix(fieldName: string) {
    return (
      this.apiKeyForm.value[fieldName].length >= actStr.length &&
      !this.apiKeyForm.controls[fieldName].valid &&
      !this.apiKeyForm.value[fieldName].startsWith(actStr)
    )
  }

  get badPrimaryAccountIdPrefix() {
    return this.isBadAccountIdPrefix('primaryAccountId')
  }

  get badRecoveryAccountIdPrefix() {
    return this.isBadAccountIdPrefix('recoveryAccountId')
  }

  private isBadAccountIdLength(fieldName: string) {
    let example = 'act-12345678'
    return this.apiKeyForm.value[fieldName].length > example.length
  }

  get badPrimaryAccountIdLength() {
    return this.isBadAccountIdLength('primaryAccountId')
  }

  get badRecoveryAccountIdLength() {
    return this.isBadAccountIdLength('recoveryAccountId')
  }

  private isBadAccountIdChars(fieldName: string) {
    return !this.apiKeyForm.value[fieldName].substring(actStr.length).match(/^[a-zA-Z0-9]*$/)
  }

  get badPrimaryAccountIdChars() {
    return this.isBadAccountIdChars('primaryAccountId')
  }

  get badRecoveryAccountIdChars() {
    return this.isBadAccountIdChars('recoveryAccountId')
  }
}
